/**
 * KED-1721 Create date picker
 * https://kpmg-au-ddx.atlassian.net/browse/KED-1721
 *
 * This component uses the pickadate lib:
 * http://amsul.ca/pickadate.js/api/
 */
import {AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation} from "@angular/core";
import {UntypedFormControl, ValidatorFn, Validators} from "@angular/forms";
import {IAnswer, IFormField} from "../form-field-wrapper/form-field-wrapper.component";
import moment from "moment";

declare var $: any;
declare var pickadate: any;

export const dateFieldFormat: string = "DD/MM/YYYY";

@Component({
    selector: "app-date-field",
    templateUrl: "./date-field.component.html",
    encapsulation: ViewEncapsulation.None, // allow pickadate styles to be applied
    styleUrls: ["./date-field.component.scss"],
    standalone: false
})
export class DateFieldComponent implements OnInit, AfterViewInit {
  @Input() myFormControl: UntypedFormControl;
  @Input() question: IFormField;
  @Input() disabled?: boolean;
  @Input() tabIndex?: number = 0;
  @ViewChild("datePicker") datePicker: ElementRef;
  dateFormat = dateFieldFormat;
  pickadate: any;
  // pickadate config
  config: any

  constructor() {
  }

  ngOnInit() {
  }

  // init the date picker
  ngAfterViewInit() {
    if (this.question.valueFormat) this.dateFormat = this.question.valueFormat
    this.config = {
      format: this.dateFormat,
      selectYears: true,
      selectMonths: true,
      weekdaysShort: ["S", "M", "T", "W", "T", "F", "S"],

      onSet: (val: {
        select?: string; // key only returns when a date has been set
        clear?: null; // key only returns if picker is 'cleared'
      }) => {
        if ("select" in val) {
          let date = "";
          // when a date is manually set from input, pickadate returns the format DD/MM/YYYY
          if (typeof val.select === "string") date = moment(val.select, this.dateFormat).format(this.dateFormat);
          // when a date is selected from the datepicker, the value is returned in milliseconds
          else if (typeof val.select === "number") date = moment(val.select).format(this.dateFormat);
          this.myFormControl.setValue(date);
        } else if ("clear" in val) {
          // when datepicker is 'cleared'
          this.myFormControl.setValue("");
        }
      },
    };
    if (this.question.minDate)
      this.config["min"] = moment(this.question.minDate, this.dateFormat).format(this.dateFormat);
    if (this.question.maxDate)
      this.config["max"] = moment(this.question.maxDate, this.dateFormat).format(this.dateFormat);

    const picker = $(this.datePicker.nativeElement).pickadate(this.config);
    this.pickadate = picker.pickadate("picker");
    this.pickadate.on('set', () => {
      const answer = this.question.answers.length && this.question.answers[0];
      const originalAnswer = answer && answer.answer != null ? answer.answer : '';
      if (this.myFormControl.value !== originalAnswer)
        this.myFormControl.markAsDirty();
      else
        this.myFormControl.markAsPristine();
    });

  }

  // sets the date on pickadate popup
  setDate() {
    const answer = this.question.answers.length && this.question.answers[0];
    const originalAnswer = answer && answer.answer != null ? answer.answer : '';

    if (this.myFormControl.value !== originalAnswer)
      this.myFormControl.markAsDirty();
    else
      this.myFormControl.markAsPristine();
    if (this.myFormControl.value && this.myFormControl.valid)
      this.pickadate.set("select", moment(this.myFormControl.value, this.dateFormat).format(this.dateFormat), {
        format: this.dateFormat,
      });
  }
}

const checkValueFormat = (question: IFormField) => {
  return question.valueFormat? question.valueFormat: dateFieldFormat
}

// validate minimum date
const minDate = (question: IFormField): ValidatorFn => {
  const valueFormat = checkValueFormat(question)
  return formControl => {
    const isValid =
      formControl.value &&
      moment(formControl.value, valueFormat).diff(moment(question.minDate, valueFormat), "days") >= 0;
    return !formControl.value || isValid
      ? null
      : {
        minDate: {
          valid: false,
        },
      };
  };
};

// validate maximum date
const maxDate = (question: IFormField): ValidatorFn => {
  const valueFormat = checkValueFormat(question)
  return formControl => {
    const isValid =
      formControl.value &&
      moment(formControl.value, valueFormat).diff(moment(question.maxDate, valueFormat), "days") <= 0;
    return !formControl.value || isValid
      ? null
      : {
        maxDate: {
          valid: false,
        },
      };
  };
};

const dateFieldValidators = (question: IFormField) => {
  const validators = [];
  if (question.mandatory) validators.push(Validators.required);
  // vaildator for date format DD/MM/YYY
  const valueFormat = checkValueFormat(question)
  const regex = valueFormat.replace('DD', '\\d{2}' ).replace('MM', '\\d{2}').replace('YYYY', '\\d{4}')
  validators.push(Validators.pattern("^" + regex + "$"));
  validators.push(Validators.maxLength(10));
  if (question.maxDate) validators.push(maxDate(question));
  if (question.minDate) validators.push(minDate(question));
  return validators;
};

export const makeDateFieldFormControl = (q: IFormField, answer?: IAnswer) => {
  const formControlValue = answer ? answer.answer : (q.default != null ? String(q.default) : "");
  q.validators = dateFieldValidators(q);
  const formControl = new UntypedFormControl(formControlValue, {validators: Validators.compose(q.validators)});
  if (!answer && formControlValue) {
    formControl.markAsDirty();
  }
  return formControl;
};
